import React, { useEffect, useRef, useState } from 'react';
import { Typography, Tab, Tabs, Button, CircularProgress } from '@mic3/platform-ui';
import PageTemplate from 'app/components/templates/PageTemplate';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import styled, { css } from 'styled-components';
import GeocodingAdminSection from 'app/containers/Maps/SituationalAwareness/GeocodingAdminSection';
import BaseLayersAdminSection from 'app/containers/Maps/SituationalAwareness/BaseLayersAdminSection';
import Icon from 'app/components/atoms/Icon/Icon';
import { loadMapSystemSettings, setDocumentTitle, updateMapSystemSettings } from 'store/actions/app/appActions';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'app/components/atoms/Loader/Loader';
import { modulesAndPageTitles } from 'app/config/typesConfig';
import { getPermissions } from 'app/config/rolesConfig';
import { MAP_SETTINGS, generateAvailableLayers } from 'app/utils/maps/layer/layerUtils';

const sharedButtonStyles = css`
    font-size: 12px !important;
    font-style: normal;
    font-weight: 500;
    line-height: 12px;
    padding: 3px 8px !important;
    height: 36.5px !important;
    letter-spacing: 0.42px;
    width: ${({ width }) => width} !important;
    min-width: ${({ minWidth }) => minWidth} !important;
    margin-left: 10px !important;
`;
const ButtonStyled = styled(Button)`
    ${sharedButtonStyles}
`;

const IconStyled = styled(Icon)`
    &::before {
        font-size: ${({ fontSize }) => fontSize} !important;
    }
`;

const StyledTypography = styled(Typography)`
    color: ${({ theme }) => theme.material.colors.text.primary};
`;

const StyledTab = styled(Tab)`
    color: ${({ theme }) => theme.material.colors.text.secondary} !important;
    &.Mui-selected {
        color: ${({ theme }) => theme.material.colors.text.landingActiveButton} !important;
    }
`;



const SiteMapsSettings = () => {
    const formRef = useRef();

    const mapSettings = useSelector((state) => state.app.mapSettings);
    const mapSettingsLoading = useSelector((state) => state.app.mapSettingsLoading);

    const [activeTab, setActiveTab] = useState(0);
    const [formState, setFormState] = useState({});
    const [formRevertKey, setFormRevertKey] = useState(0);
    const [formTouched, setFormTouched] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const dispatch = useDispatch();
    const [isUnCollapsed, setUnCollapsed] = useState(true);

    useEffect(() => {
        if (mapSettingsLoading) return;
        setFormState({ ...(mapSettings || {}) });
    }, [mapSettings, mapSettingsLoading]);

    const revertChanges = () => {
        setFormRevertKey((prevKey) => prevKey + 1);
        setFormState({ ...(mapSettings || {}) });
        setFormTouched((prev) => ({ ...prev, [activeTab]: false }));
    };

    useEffect(() => {
        dispatch(loadMapSystemSettings());
    }, [dispatch]);

    const onFormChange = (data, { name, value }) => {
        const formData = { ...(data || {}) };
        
        if ((name === MAP_SETTINGS.ENABLE_BUILT_IN_BASE_LAYERS || name === MAP_SETTINGS.ENABLE_GOOGLE_MAPS)) {
            const availableLayers = generateAvailableLayers(formData);
            const availableLayerValues = availableLayers.map(layer => layer.value);
            if (formData.defaultBaseLayer && !availableLayerValues.includes(formData.defaultBaseLayer)) {
                formData.defaultBaseLayer = null;
            }
            if (formData.defaultMapBaseLayer && !availableLayerValues.includes(formData.defaultMapBaseLayer)) {
                formData.defaultMapBaseLayer = null;
            }
        }
        
        setFormState(formData);
        setFormTouched((prev) => ({ ...prev, [activeTab]: true }));
    };

    useEffect(() => {
        dispatch(setDocumentTitle(modulesAndPageTitles.adminConsole.site.maps));
    }, [dispatch]);

    const { canEdit } = getPermissions(mapSettings?.role) || {};

    const handleTabChange = () => {
        switch (activeTab) {
            case 0:
                return (
                    <BaseLayersAdminSection
                        onChange={onFormChange}
                        data={formState}
                        formRef={formRef}
                        isUnCollapsed={isUnCollapsed}
                        addNewBaseLayer={addNewBaseLayer}
                        setFormState={setFormState}
                        formRevertKey={formRevertKey}
                        canEdit={canEdit}
                    />
                );
            case 1:
                return (
                    <GeocodingAdminSection
                        onChange={onFormChange}
                        data={formState}
                        formRef={formRef}
                        isUnCollapsed={isUnCollapsed}
                        formRevertKey={formRevertKey}
                        canEdit={canEdit}
                    />
                );
            default:
                break;
        }
    };
    
    const saveSettings = () => {
        setIsSaving(true);
        formRef.current.isValidForm().then(async ({ data, errors }) => {
            if (!errors) {
                try {
                    await dispatch(updateMapSystemSettings(data));
                    dispatch(loadMapSystemSettings());
                } catch (error) {}
            }
            setIsSaving(false);

        });
    };

    const addNewBaseLayer = (data) => {
        setFormState((prevState) => ({ ...prevState, baseLayers: [...(prevState.baseLayers || []), data] }));
    };

    const renderSaveButton = () => {
        return (
            <>
                {canEdit ? (
                    <>
                        <Button 
                            key={113} 
                            onClick={saveSettings} 
                            color='primary' 
                            form='form' 
                            type='submit' 
                            style={{ marginRight: '10px' }}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} color="primary" /> : 'Save'}
                        </Button>
                        <Button disabled={!formTouched[activeTab]} variant='outlined' onClick={revertChanges} color='primary' form='form'>
                            Revert
                        </Button>{' '}
                    </>
                ) : null}

                <ButtonStyled variant='outlined' minWidth='38px' size='small' onClick={() => setUnCollapsed(!isUnCollapsed)}>
                    <IconStyled
                        size='sm'
                        hexColor='#7391D0'
                        fontSize='20px'
                        name={isUnCollapsed ? 'unfold-less-horizontal' : 'unfold-more-horizontal'}
                    />
                </ButtonStyled>
            </>
        );
    };

    return (
        <PageTemplate title='Site_Maps'>
            <HeaderBar left={<StyledTypography variant='h6'>{modulesAndPageTitles.adminConsole.site.maps}</StyledTypography>} />
            <ContentArea>
                <HeaderBar
                    isForward
                    right={renderSaveButton()}
                    left={
                        <Tabs variant='scrollable' value={activeTab} onChange={(e, value) => setActiveTab(value)}>
                            <StyledTab label={'Base Layers'} />
                            <StyledTab label={'Geocoding'} />
                        </Tabs>
                    }
                />
                {mapSettingsLoading && <Loader absolute />}
                {handleTabChange()}
            </ContentArea>
        </PageTemplate>
    );
};

export default SiteMapsSettings;
